import { useEffect, useState, createContext, useContext } from 'react';
import { useToast } from '../context/ToastContext';

interface NetworkErrorContextType {
  triggerNetworkError: (message?: string) => void;
  resetNetworkError: () => void;
}

const NetworkErrorContext = createContext<NetworkErrorContextType | undefined>(undefined);

export function useNetworkError() {
  const context = useContext(NetworkErrorContext);
  if (!context) {
    throw new Error('useNetworkError must be used within a NetworkErrorBoundary');
  }
  return context;
}

interface NetworkErrorBoundaryProps {
  children: React.ReactNode;
}

export function NetworkErrorBoundary({ children }: NetworkErrorBoundaryProps) {
  const [, setIsOffline] = useState(!navigator.onLine);
  const [, setForcedError] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      showToast('You are currently offline. Please check your internet connection.', 'error');
    };
    
    const handleOnline = () => {
      setIsOffline(false);
      setForcedError(null);
      showToast('You are back online.', 'success');
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [showToast]);

  const triggerNetworkError = (message?: string) => {
    setForcedError(message || "We couldn't reach our servers. Please check your internet connection and try again.");
    showToast(message || 'Network error occurred', 'error');
  };

  const resetNetworkError = () => {
    setForcedError(null);
    setIsOffline(!navigator.onLine);
  };

  return (
    <NetworkErrorContext.Provider value={{ triggerNetworkError, resetNetworkError }}>
      {children}
    </NetworkErrorContext.Provider>
  );
}
